import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import Table from "../../../core/common/dataTable/index";
import Preloader from "../../../core/common/loader/preLoader";
import Toasts from "../../../core/common/toast";
import { add, fetchAll, update } from "../../../core/data/redux/workShiftSlice";
import { DateFormat } from "../../../core/hooks/dateFormat";
import { useDebounce } from "../../../core/hooks/useDebounce";
import { all_routes } from "../../router/all_routes";
import PermissionGuard from "../../permissionGuard";
import { isSupportViewer } from "../../../utils/permissionUtils";
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

const hourOptions = Array.from({ length: 13 }, (_, i) => ({
  label: `${i} Hr`,
  value: String(i),
}));

const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  label: `${i} Min`,
  value: String(i),
}));

const parseMinutesToHoursAndMinutes = (
  val: any
): { hours: number; minutes: number } => {
  if (val === undefined || val === null || val === "") {
    return { hours: 0, minutes: 0 };
  }
  const str = String(val).trim();
  if (str.includes(":")) {
    const [h, m] = str.split(":").map(Number);
    const total = (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
    return {
      hours: Math.min(12, Math.max(0, Math.floor(total / 60))),
      minutes: Math.min(59, Math.max(0, total % 60)),
    };
  }
  const totalMin = parseInt(str, 10);
  if (isNaN(totalMin) || totalMin < 0) {
    return { hours: 0, minutes: 0 };
  }
  return {
    hours: Math.min(12, Math.floor(totalMin / 60)),
    minutes: Math.min(59, totalMin % 60),
  };
};

const schema = Yup.object().shape({
  shift_name: Yup.string()
    .required("Shift name is required")
    .min(2, "Shift name must be at least 2 characters long")
    .max(50, "Shift name cannot exceed 50 characters")
    .test("no-html-js", "Invalid characters in Shift Name", function (value) {
      if (!value) return true;

      // Check for HTML tags (including script, img, div, span, etc.)
      const htmlTagPattern = /<\s*\/?[a-zA-Z][^>]*>/i;

      // Check for JavaScript keywords and patterns
      const jsPatterns = [
        /\bscript\b/i,
        /\balert\b/i,
        /\bconsole\b/i,
        /\bdocument\b/i,
        /\bwindow\b/i,
        /\beval\b/i,
        /\bfunction\b/i,
        /\bvar\b/i,
        /\blet\b/i,
        /\bconst\b/i,
        /javascript:/i,
        /vbscript:/i,
        /onload/i,
        /onclick/i,
        /onmouseover/i,
        /onerror/i,
      ];

      // Check for HTML tags
      if (htmlTagPattern.test(value)) {
        return false;
      }

      // Check for JavaScript patterns
      for (const pattern of jsPatterns) {
        if (pattern.test(value)) {
          return false;
        }
      }

      return true;
    })
    .test(
      "no-special-chars",
      "Invalid characters in Shift Name",
      function (value) {
        if (!value) return true;
        // Allow letters, numbers, spaces, hyphens, underscores, parentheses, and periods
        // Reject @, #, $, %, ^, &, *, +, =, <, >, {, }, [, ], |, \, :, ;, ", ', ?, /, ~, `
        const invalidChars = /[@#$%^&*+=<>{}[\]|\\:;"'?/~`]/;
        return !invalidChars.test(value);
      }
    )
    .trim(),
  shift_code: Yup.string()
    .required("Shift code is required")
    .matches(
      /^[a-zA-Z0-9\-_]+$/,
      "Shift code can only contain letters, numbers, hyphens, and underscores"
    )
    .min(2, "Shift code must be at least 2 characters long")
    .max(20, "Shift code cannot exceed 20 characters")
    .trim(),
  shift_description: Yup.string()
    .trim()
    .notRequired(),
  start_time: Yup.string().required("Start time is required"),
  end_time: Yup.string().required("End time is required"),
  attendance_grace_time_hours: Yup.string().required("Hour is required"),
  attendance_grace_time_minutes: Yup.string().required("Minute is required"),
  attendance_margin_time_hours: Yup.string().required("Hour is required"),
  attendance_margin_time_minutes: Yup.string().required("Minute is required"),
  shift_end_grace_time_hours: Yup.string().required("Hour is required"),
  shift_end_grace_time_minutes: Yup.string().required("Minute is required"),
  attendance_grace_time: Yup.string().notRequired(),
  attendance_margin_time: Yup.string().notRequired(),
  shift_end_grace_time: Yup.string().notRequired(),
  break_time_in_minutes: Yup.string().required(
    "Break time in minutes is required"
  ),
  checkout_type: Yup.string().required("Checkout type is required"),
  half_day_start_time: Yup.string().required("Half day start time is required"),
  is_active: Yup.boolean(),
});

const checkoutTypeDropdown = [
  { label: "AUTOMATIC", value: "AUTOMATIC" },
  { label: "MANUALLY", value: "MANUALLY" },
];

const shiftNameOptions = [
  { label: "Morning Shift", value: "Morning Shift" },
  { label: "General Shift", value: "General Shift" },
  { label: "Afternoon Shift", value: "Afternoon Shift" },
  { label: "Night Shift", value: "Night Shift" },
  { label: "Other", value: "Other" },
];

const timeDropdown = [
  { label: "10 min", value: "10" },
  { label: "15 min", value: "15" },
  { label: "20 min", value: "20" },
  { label: "25 min", value: "25" },
  { label: "30 min", value: "30" },
  { label: "35 min", value: "35" },
  { label: "40 min", value: "40" },
  { label: "45 min", value: "45" },
  { label: "50 min", value: "50" },
  { label: "55 min", value: "55" },
  { label: "60 min", value: "60" },
];

const WorkShifts = () => {
  const [currentItem, setCurrentItem] = useState<any>(null);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const workShiftState = useSelector((state: any) => state.workShiftState);
  const routes = all_routes;
  const [dataList, setDataList] = React.useState<any[]>([]);
  const [filteredDataList, setFilteredDataList] = React.useState<any[]>([]);
  const [searchText, setSearchText] = React.useState<string>("");
  const [statusFilter, setStatusFilter] = React.useState<string>("all"); // "all", "active", "inactive"

  const [allowedPermissions, setAllowedPermissions] = React.useState<any[]>([]);
  const [userType, setUserType] = React.useState("");

  // Toast state variables (following office location pattern)
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      start_time: "",
      end_time: "",
      attendance_grace_time_hours: "0",
      attendance_grace_time_minutes: "0",
      attendance_margin_time_hours: "0",
      attendance_margin_time_minutes: "0",
      shift_end_grace_time_hours: "0",
      shift_end_grace_time_minutes: "0",
      break_time_in_minutes: "",
      shift_name: "",
      shift_code: "",
      shift_description: "",
      checkout_type: "",
      attendance_grace_time: "0",
      attendance_margin_time: "0",
      shift_end_grace_time: "0",
      half_day_start_time: "",
      is_active: false,
    },
  });

  // Helper function to filter input characters for shift code
  const handleShiftCodeInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = e.key;
    // Allow backspace, delete, tab, escape, enter, arrow keys
    if (
      char === "Backspace" ||
      char === "Delete" ||
      char === "Tab" ||
      char === "Escape" ||
      char === "Enter" ||
      char.includes("Arrow")
    ) {
      return;
    }
    // Allow letters, numbers, hyphens, underscores (no spaces for code)
    if (!/^[a-zA-Z0-9\-_]$/.test(char)) {
      e.preventDefault();
    }
  };

  const onSubmit = async (data: any) => {
    try {
      console.log("currentItem", currentItem);

      const attendanceGraceHours = parseInt(
        data.attendance_grace_time_hours || "0",
        10
      );
      const attendanceGraceMins = parseInt(
        data.attendance_grace_time_minutes || "0",
        10
      );
      const attendanceGraceTotal = String(
        attendanceGraceHours * 60 + attendanceGraceMins
      );

      const attendanceMarginHours = parseInt(
        data.attendance_margin_time_hours || "0",
        10
      );
      const attendanceMarginMins = parseInt(
        data.attendance_margin_time_minutes || "0",
        10
      );
      const attendanceMarginTotal = String(
        attendanceMarginHours * 60 + attendanceMarginMins
      );

      const shiftEndGraceHours = parseInt(
        data.shift_end_grace_time_hours || "0",
        10
      );
      const shiftEndGraceMins = parseInt(
        data.shift_end_grace_time_minutes || "0",
        10
      );
      const shiftEndGraceTotal = String(
        shiftEndGraceHours * 60 + shiftEndGraceMins
      );

      const payload: any = {
        ...data,
        attendance_grace_time: attendanceGraceTotal,
        attendance_margin_time: attendanceMarginTotal,
        shift_end_grace_time: shiftEndGraceTotal,
      };

      delete payload.attendance_grace_time_hours;
      delete payload.attendance_grace_time_minutes;
      delete payload.attendance_margin_time_hours;
      delete payload.attendance_margin_time_minutes;
      delete payload.shift_end_grace_time_hours;
      delete payload.shift_end_grace_time_minutes;

      if (currentItem && currentItem.id) {
        payload["id"] = currentItem.id;
        await dispatch(update(payload));
      } else {
        await dispatch(add(payload));
      }

      // Reset form and close modal
      //await fetchRecords();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const fetchRecords = () => {
    dispatch(
      fetchAll({ page: pagination.current, limit: pagination.pageSize })
    );
  };

  // Apply both search and status filters
  const applyFilters = useCallback(
    (searchValue: string, statusValue: string) => {
      let filteredData = [...dataList];

      // Apply status filter first
      if (statusValue !== "all") {
        filteredData = filteredData.filter((record) => {
          if (statusValue === "active") return record.is_active === true;
          if (statusValue === "inactive")
            return record.is_active === false || record.is_active === null;
          return true;
        });
      }

      // Apply search filter
      if (searchValue.trim()) {
        const searchTerm = searchValue.toLowerCase().trim();
        filteredData = filteredData.filter((record) => {
          return (
            record.shift_name?.toLowerCase().includes(searchTerm) ||
            record.shift_code?.toLowerCase().includes(searchTerm) ||
            record.shift_description?.toLowerCase().includes(searchTerm) ||
            record.checkout_type?.toLowerCase().includes(searchTerm) ||
            record.start_time?.toLowerCase().includes(searchTerm) ||
            record.end_time?.toLowerCase().includes(searchTerm) ||
            (record.is_active &&
              ["active", "enabled", "true"].some((term) =>
                term.includes(searchTerm)
              )) ||
            (!record.is_active &&
              ["inactive", "disabled", "false"].some((term) =>
                term.includes(searchTerm)
              ))
          );
        });
      }

      setFilteredDataList(filteredData);
    },
    [dataList]
  );

  // Debounced filter function (300ms delay for local filtering)
  const debouncedApplyFilters = useDebounce(applyFilters, 300);

  // Handle search functionality with status filter
  const handleSearch = (value: string) => {
    setSearchText(value);
    debouncedApplyFilters(value, statusFilter);
  };

  // Handle status filter
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    debouncedApplyFilters(searchText, status);
  };

  // Update filtered data when main data changes
  React.useEffect(() => {
    applyFilters(searchText, statusFilter);
  }, [dataList, applyFilters, searchText, statusFilter]);

  React.useEffect(() => {
    let allowedPermissions: any = [];

    if (
      localStorage.getItem("allowed_permissions") != undefined &&
      localStorage.getItem("allowed_permissions") != "undefined"
    ) {
      allowedPermissions = JSON.parse(
        localStorage.getItem("allowed_permissions") || "[]"
      );
    }
    const user: any = localStorage.getItem("user");
    const userJson = user && user != "undefined" ? JSON.parse(user) : {};
    setUserType(userJson?.role || "");
    setAllowedPermissions(allowedPermissions);
    fetchRecords();
  }, []);

  React.useEffect(() => {
    if (
      workShiftState.loading === "fulfilled" &&
      workShiftState.action === "update"
    ) {
      // Show success toast message
      const message =
        workShiftState?.data?.data?.message || "Shift updated successfully";
      setSuccessMessage(message);
      setShowSuccessToast(true);

      reset();
      setIsModalOpen(false);
      setCurrentItem(null);

      // Auto-hide success toast and refresh data
      setTimeout(() => {
        setShowSuccessToast(false);
        fetchRecords();
      }, 2000);
    } else if (
      workShiftState.loading === "rejected" &&
      workShiftState.action === "update"
    ) {
      // Show error toast message
      setErrorMessage(workShiftState.error || "Failed to update shift");
      setShowErrorToast(true);

      // Auto-hide error toast
      setTimeout(() => {
        setShowErrorToast(false);
      }, 4000);
    } else if (
      workShiftState.loading === "fulfilled" &&
      workShiftState.action === "add"
    ) {
      // Show success toast message
      const message =
        workShiftState?.data?.data?.message || "Shift created successfully";
      setSuccessMessage(message);
      setShowSuccessToast(true);

      reset();
      setIsModalOpen(false);
      setCurrentItem(null);

      // Auto-hide success toast and refresh data
      setTimeout(() => {
        setShowSuccessToast(false);
        fetchRecords();
      }, 2000);
    } else if (
      workShiftState.loading === "rejected" &&
      workShiftState.action === "add"
    ) {
      // Show error toast message
      setErrorMessage(workShiftState.error || "Failed to create shift");
      setShowErrorToast(true);

      // Auto-hide error toast
      setTimeout(() => {
        setShowErrorToast(false);
      }, 4000);
    } else if (
      workShiftState.loading === "fulfilled" &&
      workShiftState.action === "list"
    ) {
      const result = workShiftState?.data?.data || null;
      if (result.data && result.data.length > 0) {
        const roles = result?.data || [];
        setDataList(roles);
        setPagination({
          ...pagination,
          total: result.total || roles.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [workShiftState.loading, workShiftState.action]);

  // const handlePagination = (paginationInfo: any) => {
  //   const newPagination = {
  //     current: paginationInfo.page || paginationInfo.current,
  //     pageSize: paginationInfo.pageSize,
  //     total: paginationInfo.total || pagination.total,
  //   };
  //   setPagination(newPagination);

  //   dispatch(
  //     fetchAll({
  //       page: newPagination.current,
  //       limit: newPagination.pageSize,
  //     })
  //   );
  // };

  const handlePagination = (newPagination: any) => {
    setPagination((prev) => {
      const isPageSizeChanged = prev.pageSize !== newPagination.pageSize;
      const updatedPagination = {
        current: isPageSizeChanged ? 1 : newPagination.current || 1,
        pageSize: newPagination.pageSize,
        total: newPagination.total || prev.total,
      };
      dispatch(
        fetchAll({
          page: updatedPagination.current,
          limit: updatedPagination.pageSize,
        })
      );
      return updatedPagination;
    });
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },
    {
      title: "Shift Name",
      dataIndex: "shift_name",
      sorter: (a: any, b: any) => a.shift_name.length - b.shift_name.length,
    },
    {
      title: "Shift Code",
      dataIndex: "shift_code",
      sorter: (a: any, b: any) => a.shift_code.length - b.shift_code.length,
    },
    {
      title: "Checkout Type",
      dataIndex: "checkout_type",
      sorter: (a: any, b: any) =>
        a.checkout_type.length - b.checkout_type.length,
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      sorter: (a: any, b: any) => a.start_time.length - b.start_time.length,
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      sorter: (a: any, b: any) => a.end_time.length - b.end_time.length,
    },
    {
      title: "Attendance Grace Time",
      dataIndex: "attendance_grace_time",
      sorter: (a: any, b: any) =>
        a.attendance_grace_time.length - b.attendance_grace_time.length,
    },
    {
      title: "Break Time",
      dataIndex: "break_time_in_minutes",
      sorter: (a: any, b: any) =>
        a.break_time_in_minutes.length - b.break_time_in_minutes.length,
    },
    {
      title: "Shift Description",
      dataIndex: "shift_description",
      sorter: (a: any, b: any) =>
        a.shift_description.length - b.shift_description.length,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      sorter: (a: any, b: any) => (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0),
      render: (is_active: boolean) => (
        <div className="d-flex align-items-center">
          <span
            className={`badge ${is_active ? "badge-success-light" : "badge-danger-light"
              } fs-12 fw-medium`}
            style={{
              background: is_active ? "#e8f5e8" : "#ffeaea",
              color: is_active ? "#28a745" : "#dc3545",
              border: `1px solid ${is_active ? "#28a745" : "#dc3545"}`,
              borderRadius: "4px",
              padding: "4px 8px",
            }}
          >
            {is_active ? "Active" : "Inactive"}
          </span>
        </div>
      ),
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      sorter: (a: any, b: any) => a.created_at.length - b.created_at.length,
      render: (created_at: any) => DateFormat(created_at) || "",
    },
    ...(!isSupportViewer()
      ? [
          {
            title: "Action",
            dataIndex: "action",
            render: (text: string, record: any) => (
              <>
                <div className="d-flex align-items-center">
                  {(allowedPermissions.includes("work-shift/edit") ||
                    userType === "ADMIN" ||
                    userType === "SUBADMIN") && (
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle  p-0 me-2"
                      onClick={() => {
                        console.log("record", record);
                        setIsModalOpen(true);
                        setValue("shift_name", record.shift_name);
                        setValue("shift_code", record.shift_code);
                        setValue("shift_description", record.shift_description);
                        setValue("start_time", record.start_time);
                        setValue("end_time", record.end_time);
                        setValue("checkout_type", record.checkout_type);
                        setValue(
                          "break_time_in_minutes",
                          record.break_time_in_minutes
                        );
                        const priorParsed = parseMinutesToHoursAndMinutes(
                          record.attendance_grace_time
                        );
                        setValue(
                          "attendance_grace_time_hours",
                          String(priorParsed.hours)
                        );
                        setValue(
                          "attendance_grace_time_minutes",
                          String(priorParsed.minutes)
                        );

                        const postParsed = parseMinutesToHoursAndMinutes(
                          record.attendance_margin_time
                        );
                        setValue(
                          "attendance_margin_time_hours",
                          String(postParsed.hours)
                        );
                        setValue(
                          "attendance_margin_time_minutes",
                          String(postParsed.minutes)
                        );

                        const checkoutParsed = parseMinutesToHoursAndMinutes(
                          record.shift_end_grace_time
                        );
                        setValue(
                          "shift_end_grace_time_hours",
                          String(checkoutParsed.hours)
                        );
                        setValue(
                          "shift_end_grace_time_minutes",
                          String(checkoutParsed.minutes)
                        );

                        setValue("half_day_start_time", record.half_day_start_time);
                        setValue("is_active", record.is_active);

                        setCurrentItem(record);
                      }}
                    >
                      <i className="ti ti-edit-circle text-primary" />
                    </Link>
                  )}
                </div>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <>
        {workShiftState.loading === "pending" &&
          (workShiftState.action === "add" ||
            workShiftState.action === "update") && <Preloader />}

        {/* Toast Messages */}
        <Toasts
          show={showErrorToast}
          message={errorMessage}
          title={"Failed"}
          type={"danger"}
        />
        <Toasts
          show={showSuccessToast}
          message={successMessage}
          title={"Success"}
          type={"success"}
        />

        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Work Shifts</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Work Shifts</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Manage Work Shifts
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="mb-2">
                  <PermissionGuard route="work-shift/add">
                    <Link
                      to="#"
                      className="btn btn-primary d-flex align-items-center"
                      onClick={() => {
                        setCurrentItem(null);
                        reset(); // Reset the entire form to default values
                        setIsModalOpen(true);
                      }}
                    >
                      <i className="ti ti-square-rounded-plus me-2" />
                      Add Shift
                    </Link>
                  </PermissionGuard>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            {/* Filter Section */}
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3">Manage Work Shifts</h4>
              </div>
              <div className="card-body p-0 py-3">
                {/* Role Permission List */}
                <Table
                  columns={columns}
                  dataSource={filteredDataList}
                  Selection={false}
                  paginationConfig={pagination}
                  handlePagination={handlePagination}
                  handleSearch={handleSearch}
                  loading={
                    workShiftState.loading === "pending" &&
                    workShiftState.action === "list"
                  }
                />
                {/* /Role Permission List */}
              </div>
            </div>
            {/* /Filter Section */}
          </div>
        </div>
        {/* /Page Wrapper */}
        {/* Add Role */}
        <Modal
          title={currentItem ? "Edit Shift" : "Add Shift"}
          open={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
            reset();
            setCurrentItem(null);
          }}
          footer={null}
          destroyOnClose
          width={700}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">Shift Name</label>
                    <select
                      className="form-select"
                      {...register("shift_name")}
                      onChange={(e) => {
                        setValue("shift_name", e.target.value);
                      }}
                    >
                      <option value="">Select shift name</option>
                      {shiftNameOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.shift_name && (
                      <small className="text-danger">
                        {errors.shift_name.message}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">Shift Code</label>
                    <input
                      type="text"
                      className="form-control"
                      {...register("shift_code")}
                      onKeyPress={handleShiftCodeInput}
                      maxLength={20}
                      placeholder="Enter shift code"
                      style={{ textTransform: "uppercase" }}
                    />
                    {errors.shift_code && (
                      <small className="text-danger">
                        {errors.shift_code.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">Start Time</label>
                    <div className="input-group time">
                      <TimePicker
                        format="HH:mm"
                        className="form-control timepicker"
                        value={
                          watch("start_time")
                            ? dayjs(watch("start_time"), "HH:mm")
                            : null
                        }
                        onChange={(time, timeString: string | string[]) =>
                          setValue(
                            "start_time",
                            Array.isArray(timeString)
                              ? timeString.join(",")
                              : timeString
                          )
                        }
                      />
                      <span className="input-group-text">
                        <i className="fa-regular fa-clock" />
                      </span>
                    </div>
                    {errors.start_time && (
                      <small className="text-danger">
                        {errors.start_time.message}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">End Time</label>
                    <div className="input-group time">
                      <TimePicker
                        format="HH:mm"
                        className="form-control timepicker"
                        value={
                          watch("end_time")
                            ? dayjs(watch("end_time"), "HH:mm")
                            : null
                        }
                        onChange={(time, timeString: string | string[]) =>
                          setValue(
                            "end_time",
                            Array.isArray(timeString)
                              ? timeString.join(",")
                              : timeString
                          )
                        }
                      />
                      <span className="input-group-text">
                        <i className="fa-regular fa-clock" />
                      </span>
                    </div>
                    {errors.end_time && (
                      <small className="text-danger">
                        {errors.end_time.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-0">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label mb-0">
                        Attendance Margin Time (Prior Time)
                      </label>
                      <span className="text-primary fs-12 fw-medium">
                        {(parseInt(watch("attendance_grace_time_hours") || "0", 10) * 60) +
                          parseInt(watch("attendance_grace_time_minutes") || "0", 10)}{" "}
                        mins
                      </span>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <select
                          className="form-select"
                          {...register("attendance_grace_time_hours")}
                        >
                          {hourOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.attendance_grace_time_hours && (
                          <small className="text-danger">
                            {errors.attendance_grace_time_hours.message as string}
                          </small>
                        )}
                      </div>
                      <div className="col-6">
                        <select
                          className="form-select"
                          {...register("attendance_grace_time_minutes")}
                        >
                          {minuteOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.attendance_grace_time_minutes && (
                          <small className="text-danger">
                            {errors.attendance_grace_time_minutes.message as string}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-0">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label mb-0">
                        Attendance Grace Time (Post Time)
                      </label>
                      <span className="text-primary fs-12 fw-medium">
                        {(parseInt(watch("attendance_margin_time_hours") || "0", 10) * 60) +
                          parseInt(watch("attendance_margin_time_minutes") || "0", 10)}{" "}
                        mins
                      </span>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <select
                          className="form-select"
                          {...register("attendance_margin_time_hours")}
                        >
                          {hourOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.attendance_margin_time_hours && (
                          <small className="text-danger">
                            {errors.attendance_margin_time_hours.message as string}
                          </small>
                        )}
                      </div>
                      <div className="col-6">
                        <select
                          className="form-select"
                          {...register("attendance_margin_time_minutes")}
                        >
                          {minuteOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.attendance_margin_time_minutes && (
                          <small className="text-danger">
                            {errors.attendance_margin_time_minutes.message as string}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-0">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label mb-0">
                        Checkout grace Time in Minutes
                      </label>
                      <span className="text-primary fs-12 fw-medium">
                        {(parseInt(watch("shift_end_grace_time_hours") || "0", 10) * 60) +
                          parseInt(watch("shift_end_grace_time_minutes") || "0", 10)}{" "}
                        mins
                      </span>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <select
                          className="form-select"
                          {...register("shift_end_grace_time_hours")}
                        >
                          {hourOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.shift_end_grace_time_hours && (
                          <small className="text-danger">
                            {errors.shift_end_grace_time_hours.message as string}
                          </small>
                        )}
                      </div>
                      <div className="col-6">
                        <select
                          className="form-select"
                          {...register("shift_end_grace_time_minutes")}
                        >
                          {minuteOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.shift_end_grace_time_minutes && (
                          <small className="text-danger">
                            {errors.shift_end_grace_time_minutes.message as string}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">Half Day Start Time</label>
                    <div className="input-group time">
                      <TimePicker
                        format="HH:mm"
                        className="form-control timepicker"
                        value={
                          watch("half_day_start_time")
                            ? dayjs(watch("half_day_start_time"), "HH:mm")
                            : null
                        }
                        onChange={(time, timeString: string | string[]) =>
                          setValue(
                            "half_day_start_time",
                            Array.isArray(timeString)
                              ? timeString.join(",")
                              : timeString
                          )
                        }
                      />
                      <span className="input-group-text">
                        <i className="fa-regular fa-clock" />
                      </span>
                    </div>
                    {errors.half_day_start_time && (
                      <small className="text-danger">
                        {errors.half_day_start_time.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">Break Time</label>
                    <div className="input-group time">
                      <select
                        {...register("break_time_in_minutes")}
                        className="form-control"
                      >
                        <option value="">Select Break Time</option>
                        {timeDropdown.map((day: any, index: number) => (
                          <option key={index} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                      <span className="input-group-text">
                        <i className="fa-regular fa-clock" />
                      </span>
                    </div>
                    {errors.break_time_in_minutes && (
                      <small className="text-danger">
                        {errors.break_time_in_minutes.message}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-0">
                    <label className="form-label">Checkout Type</label>
                    <div className="input-group time">
                      <select
                        {...register("checkout_type")}
                        className="form-control"
                      >
                        <option value="">Select Checkout Type</option>
                        {checkoutTypeDropdown.map(
                          (item: any, index: number) => (
                            <option key={index} value={item.value}>
                              {item.label}
                            </option>
                          )
                        )}
                      </select>
                      <span className="input-group-text">
                        <i className="fa-regular fa-clock" />
                      </span>
                    </div>
                    {errors.checkout_type && (
                      <small className="text-danger">
                        {errors.checkout_type.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-0">
                    <label className="form-label">Shift Description</label>
                    <textarea
                      className="form-control"
                      {...register("shift_description")}
                      maxLength={500}
                      rows={3}
                      placeholder="Enter shift description (optional)"
                    />
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <div>
                        {errors.shift_description && (
                          <small className="text-danger">
                            {errors.shift_description.message}
                          </small>
                        )}
                      </div>
                      <div className="text-muted small">
                        {watch("shift_description")?.length || 0}/500 characters
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-0">
                    <div>
                      <input
                        type="checkbox"
                        {...register("is_active")}
                        style={{ marginTop: 10 }}
                      />
                      <label className="form-label" style={{ marginLeft: 10 }}>
                        Status
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Link
                to="#"
                className="btn btn-light me-2"
                onClick={() => {
                  setIsModalOpen(false);
                  reset();
                  setCurrentItem(null);
                }}
              >
                Cancel
              </Link>
              <button type="submit" className="btn btn-primary">
                Submit
              </button>
            </div>
          </form>
        </Modal>
        {/* /Add Role */}
        {/* Edit Role */}
        {/* <div className="modal fade" id="edit_role">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Edit Role</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-0">
                        <label className="col-form-label">Role Name</label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue="Admin"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link to="#" className="btn btn-primary" data-bs-dismiss="modal">
                    Save Changes
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div> */}
        {/* /Edit Role */}
        {/* Delete Modal */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete all the marked items, this cant be undone
                    once you delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                    >
                      Yes, Delete
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default WorkShifts;
